import { Directive, forwardRef, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { RegisterFeatureFlagId } from '@assetaccountant/api/models/register-feature-flag-id';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { Register } from '../models/register';
import { RegisterData } from '../resolvers/register.resolver';
import { Authorizer, AUTHORIZERS } from './authorizer';
import { getRouteChangesWith } from './authorizer.helpers';

@Directive({
  selector: '[authorizeRegisterFeatureFlagInverted]',
  providers: [
    {
      provide: AUTHORIZERS,
      useExisting: forwardRef(() => AuthorizeRegisterFeatureFlagInvertedDirective),
      multi: true,
    },
  ],
  standalone: false,
})
export class AuthorizeRegisterFeatureFlagInvertedDirective implements Authorizer, OnChanges {
  @Input('authorizeRegisterFeatureFlagInverted') registerFeatureFlag?: RegisterFeatureFlagId;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('authorizeRegisterId') registerId?: number;

  readonly order = 0;

  private input$ = new BehaviorSubject<{ register: Register | null } | null>(null);
  private featureFlag$ = new BehaviorSubject<RegisterFeatureFlagId | undefined>(undefined);

  readonly changes$ = getRouteChangesWith<
    Partial<RegisterData>,
    'register',
    { register: Register | null }
  >(
    this.router,
    'register',
    (register: Register | null | undefined) => ({
      register: register || null,
    }),
    this.input$,
  );

  readonly isAuthorizedChanges = combineLatest([this.changes$, this.featureFlag$]).pipe(
    switchMap(([data, featureFlag]) => {
      if (!data || !data.register) {
        return of(false);
      }

      // If explicit registerId is provided, check if it matches
      if (this.registerId && data.register.id !== this.registerId) {
        return of(false);
      }

      const isEnabled = this.isRegisterFeatureFlagDisabled(data.register, featureFlag);
      return of(isEnabled).pipe(startWith(false));
    }),
  );

  constructor(private router: Router) {
    // Initialize with current value
    this.featureFlag$.next(this.registerFeatureFlag);
  }

  ngOnChanges() {
    // Trigger re-evaluation when inputs change
    this.featureFlag$.next(this.registerFeatureFlag);
    this.input$.next(null);
  }

  private isRegisterFeatureFlagDisabled(
    register: Register,
    featureFlag?: RegisterFeatureFlagId,
  ): boolean {
    const flagToCheck = featureFlag || this.registerFeatureFlag;

    if (!flagToCheck) {
      return true;
    }

    // Check if registerFeatureFlags exists and is an array
    if (!register.registerFeatureFlags || !Array.isArray(register.registerFeatureFlags)) {
      return true;
    }

    // Check if the flag is NOT in the array (inverted logic)
    return !register.registerFeatureFlags.includes(flagToCheck);
  }
}
